import { API_URL } from '../config';

// Pokepaste ↔ schema stat key mapping
const STAT_FROM_PASTE = {
  'HP': 'hp', 'Atk': 'atk', 'Def': 'def',
  'SpA': 'spa', 'SpD': 'spd', 'Spe': 'spe',
};
const STAT_TO_PASTE = {
  hp: 'HP', atk: 'Atk', def: 'Def',
  spa: 'SpA', spd: 'SpD', spe: 'Spe',
};

// ─── Parser ───────────────────────────────────────────────────────────────────

export function parsePokepaste(text) {
  const blocks = text.trim().split(/\r?\n[ \t]*\r?\n/);
  return blocks.map(parseBlock).filter(Boolean);
}

function parseBlock(block) {
  const lines = block.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;

  // First line: optional "Nickname (Species)" or just "Species", optional "@ Item"
  const firstLine = lines[0];
  const atIdx = firstLine.lastIndexOf(' @ ');
  const namePart = atIdx >= 0 ? firstLine.slice(0, atIdx).trim() : firstLine.trim();
  const item = atIdx >= 0 ? firstLine.slice(atIdx + 3).trim() : '';

  // Extract species from "Nickname (Species)" or plain "Species"
  const parenMatch = namePart.match(/^.+\((.+?)\)\s*$/);
  const name = parenMatch ? parenMatch[1].trim() : namePart;

  const poke = {
    pokedexId: 0,
    name,
    item,
    ability: '',
    nature: '',
    tera: '',
    moves: [],
    evs: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
    ivs: { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 },
  };

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('- ')) {
      poke.moves.push(line.slice(2).trim());
    } else if (line.startsWith('Ability: ')) {
      poke.ability = line.slice(9).trim();
    } else if (line.startsWith('Tera Type: ')) {
      poke.tera = line.slice(11).trim();
    } else if (line.startsWith('EVs: ')) {
      Object.assign(poke.evs, parseStatLine(line.slice(5).trim()));
    } else if (line.startsWith('IVs: ')) {
      Object.assign(poke.ivs, parseStatLine(line.slice(5).trim()));
    } else if (line.endsWith(' Nature')) {
      poke.nature = line.slice(0, -7).trim();
    }
    // Level, Shiny, Gender — stored in paste but not needed for our schema
  }

  return poke;
}

function parseStatLine(str) {
  const result = {};
  for (const segment of str.split('/')) {
    const m = segment.trim().match(/^(\d+)\s+(.+)$/);
    if (m) {
      const key = STAT_FROM_PASTE[m[2].trim()];
      if (key) result[key] = parseInt(m[1], 10);
    }
  }
  return result;
}

// ─── Exporter ─────────────────────────────────────────────────────────────────

export function teamToPoképaste(pokemon) {
  return pokemon.map(pokeToPaste).join('\n\n');
}

function pokeToPaste(p) {
  const lines = [];
  lines.push(p.item ? `${p.name} @ ${p.item}` : p.name);
  if (p.ability) lines.push(`Ability: ${p.ability}`);
  lines.push('Level: 50');
  if (p.tera) lines.push(`Tera Type: ${p.tera}`);

  const evParts = Object.entries(p.evs || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${v} ${STAT_TO_PASTE[k]}`);
  if (evParts.length) lines.push(`EVs: ${evParts.join(' / ')}`);

  if (p.nature) lines.push(`${p.nature} Nature`);

  const ivParts = Object.entries(p.ivs || {})
    .filter(([, v]) => v < 31)
    .map(([k, v]) => `${v} ${STAT_TO_PASTE[k]}`);
  if (ivParts.length) lines.push(`IVs: ${ivParts.join(' / ')}`);

  for (const move of (p.moves || [])) {
    if (move) lines.push(`- ${move}`);
  }
  return lines.join('\n');
}

// ─── ID Lookup ────────────────────────────────────────────────────────────────

async function lookupPokedexId(name) {
  try {
    const slug = name.toLowerCase().replace(/\s+/g, '-');
    const res = await fetch(`${API_URL}/pokemon/${encodeURIComponent(slug)}`);
    if (!res.ok) return 0;
    const data = await res.json();
    return data.pokedexId || 0;
  } catch {
    return 0;
  }
}

// Resolve pokedexIds for all pokemon in a parsed list (parallel fetches)
export async function resolvePokedexIds(parsedPokemon) {
  const ids = await Promise.all(parsedPokemon.map(p => lookupPokedexId(p.name)));
  return parsedPokemon.map((p, i) => ({ ...p, pokedexId: ids[i] }));
}
